import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { configureAmplify } from './lib/amplify';
import { ApiError } from './lib/api';
import { configUrl, loadRuntimeConfig } from './lib/config';
/*
 * Fonts are bundled and served from the console's own origin. The CSP allows fonts and styles
 * from 'self' only, which a third-party font host would break — and would send every visitor's
 * address to that host. Latin subset only: the console's copy is English.
 */
import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';
import '@fontsource/ibm-plex-sans/latin-700.css';
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';
import '@fontsource/ibm-plex-mono/latin-600.css';
import './styles/global.css';

const root = document.getElementById('root');
if (!root) throw new Error('Root element missing');

/**
 * Configuration is loaded before the first render, because Amplify must be configured before
 * any component asks about the session. Booting without it and configuring later would leave a
 * window where a signed-in user looks signed out.
 */
async function bootstrap(): Promise<void> {
  const config = await loadRuntimeConfig();
  configureAmplify(config);

  /**
   * A 401 means the session is gone, and a 400 means the request was wrong — retrying either
   * just delays the error. Only genuine transport failures are worth a second attempt.
   */
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) =>
          failureCount < 2 && !(error instanceof ApiError && error.status < 500),
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });

  createRoot(root!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

/**
 * A console that cannot read its own configuration must not render a sign-in page it could
 * never complete. Failing visibly is the honest outcome — see §6.3 and the deploy runbook.
 */
function renderBootFailure(message: string): void {
  const hint = import.meta.env.DEV
    ? `Create apps/frontend/public/config.json (copy config.json.example and fill in the values
       from the deployed stacks), or point VITE_CONFIG_URL at a deployed environment.`
    : 'This is a deployment problem. The CD pipeline writes config.json when it publishes the console.';

  root!.innerHTML = `
    <div style="max-width:42rem;margin:12vh auto;padding:0 1.5rem;font-family:system-ui,sans-serif;color:#f2f2f2">
      <h1 style="font-size:1.4rem;margin:0 0 .75rem">The console could not start</h1>
      <p style="margin:0 0 .75rem;color:#9a9a9a">${message}</p>
      <p style="margin:0;color:#9a9a9a;font-size:.875rem">${hint}</p>
    </div>`;
}

void bootstrap().catch((cause: unknown) => {
  const message = cause instanceof Error ? cause.message : `Could not load ${configUrl()}`;
  // Safe to log: the config holds no secrets, and this is the only way to debug a boot failure.
  console.error('Console boot failed:', cause);
  renderBootFailure(message);
});
